"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));

var _axios = _interopRequireDefault(require("axios"));

var _https = _interopRequireDefault(require("https"));

var _jwtDecode = _interopRequireDefault(require("jwt-decode"));

var _sleep = _interopRequireDefault(require("../../helpers/sleep"));

var _httpStatus = _interopRequireDefault(require("./http-status"));

var _httpMethods = _interopRequireDefault(require("./http-methods"));

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

class BaseHttpClient {
  constructor({
    ignoreTLSErrors,
    storageProvider
  }) {
    this.ignoreTLSErrors = ignoreTLSErrors;
    this.storageProvider = storageProvider;
  }

  createInstance(ctx, requestConfig) {
    const instance = _axios.default.create();

    instance.interceptors.request.use(config => {
      const newConfig = _objectSpread(_objectSpread({}, config), this.getRequestConfig(ctx, requestConfig));

      return newConfig;
    }, error => Promise.reject(error));
    return instance;
  }
  /** Performing a GET request
  * @param {string} url - is the server URL that will be used for the request
  * @param {object} [ctx]
  * @returns {Promise} - Promise object
  */


  get(url, ctx, config) {
    let abort;
    const request = this.createInstance(ctx, config).get(url, {
      cancelToken: new _axios.default.CancelToken(c => {
        abort = c;
      })
    });
    request.abort = abort;
    return this.resolveRequest(ctx, request, {
      method: _httpMethods.default.GET,
      url,
      ctx,
      config
    });
  }
  /** Performing a POST request
  * @param {string} url - is the server URL that will be used for the request
  * @param {string} [data] - `data` is the data to be sent as the request body
  * Only applicable for request methods 'PUT', 'POST', and 'PATCH'
  * When no `transformRequest` is set, must be of one of the following types:
  * string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
  * Browser only: FormData, File, Blob
  * Node only: Stream, Buffer
  * @param {object} [ctx]
  * @returns {Promise} - Promise object
  */


  post(url, data, ctx, config) {
    let abort;
    const request = this.createInstance(ctx, config).post(url, data, {
      cancelToken: new _axios.default.CancelToken(c => {
        abort = c;
      })
    });
    request.abort = abort;
    return this.resolveRequest(ctx, request, {
      method: _httpMethods.default.POST,
      url,
      data,
      ctx,
      config
    });
  }
  /** Performing a PUT request
  * @param {string} url - is the server URL that will be used for the request
  * @param {string} [data] - `data` is the data to be sent as the request body
  * Only applicable for request methods 'PUT', 'POST', and 'PATCH'
  * When no `transformRequest` is set, must be of one of the following types:
  * string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
  * Browser only: FormData, File, Blob
  * Node only: Stream, Buffer
  * @param {object} [ctx]
  * @returns {Promise} - Promise object
  */


  put(url, data, ctx, config) {
    const request = this.createInstance(ctx, config).put(url, data);
    return this.resolveRequest(ctx, request, {
      method: _httpMethods.default.PUT,
      url,
      data,
      ctx,
      config
    });
  }
  /**
   * Perform a DELETE request
   * @param {string} url - is the server URL that will be used for the request
   * @param [ctx]
   */


  delete(url, data, ctx, config) {
    const request = this.createInstance(ctx, config).delete(url, {
      data
    });
    return this.resolveRequest(ctx, request, {
      method: _httpMethods.default.DELETE,
      url,
      data,
      ctx,
      config
    });
  }

  resolveRequest(ctx, request, requestData) {
    const {
      config = {}
    } = requestData;

    if (config.skipResolve) {
      return request;
    }

    const promise = new Promise(resolve => {
      const response = this.sendRequest(ctx, request, requestData);
      resolve(response);
    });
    promise.abort = request.abort;
    return promise;
  }

  retryRequest(requestData) {
    const {
      url,
      data,
      method,
      ctx,
      config
    } = requestData;

    switch (method) {
      case _httpMethods.default.GET:
        return this.get(url, ctx, config);

      case _httpMethods.default.POST:
        return this.post(url, data, ctx, config);

      case _httpMethods.default.PUT:
        return this.put(url, data, ctx, config);

      case _httpMethods.default.DELETE:
        return this.delete(url, data, ctx, config);

      default:
        return null;
    }
  }

  async sendRequest(ctx, request, requestData) {
    let response;

    try {
      response = await request;

      if (!response) {
        return false;
      }

      return response;
    } catch (ex) {
      if (ex.response) {
        const {
          status
        } = ex.response; // in case we receive unauthorized error (401), we perform refresh token request
        // in order to fetch a new token

        if (status === _httpStatus.default.UNAUTHORIZED) {
          const refreshTokenSucceeded = await this.refreshToken(ctx);

          if (refreshTokenSucceeded) {
            // re-send the failed request, now that we've a new valid token
            return this.retryRequest(requestData);
          }
        }
      }

      throw ex;
    }
  }

  getRequestConfig(ctx, requestConfig) {
    const config = _objectSpread({
      httpsAgent: new _https.default.Agent({
        rejectUnauthorized: !this.ignoreTLSErrors
      })
    }, requestConfig);

    const headers = config.headers || {};
    const jwtToken = this.getJwtToken(ctx);

    if (jwtToken) {
      headers.Authorization = "Bearer ".concat(jwtToken);
    }

    if (this.getFeature) {
      const tid = this.getFeature();

      if (tid) {
        headers['x-promo-tid'] = tid;
      }
    }

    config.headers = headers;
    return config;
  }
  /**
   * @param {Object} ctx - context object
   * @param {string} redirectUrl - The url to redirect to
   */


  async redirectTo(ctx, redirectUrl) {
    if (!redirectUrl) {
      return;
    }

    if (ctx && ctx.res) {
      const {
        res
      } = ctx;
      res.writeHead(302, {
        Location: redirectUrl
      });
      res.end();
    } else {
      window.location.href = redirectUrl; // disallowing other actions from happening, until the redirect it done

      await (0, _sleep.default)(5000);
    }
  }
  /**
   * Gets token from the cookies
   * @param {Object} ctx
   * @param {string} key - token key
   */


  getToken(ctx, key) {
    if (ctx && ctx.req && ctx[key]) {
      return ctx[key];
    }

    return this.storageProvider.get(key, ctx);
  }
  /**
   * Sets token inside the cookies
   * @param {Object} ctx
   * @param {string} key - token key
   * @param {string} value - token value
   */


  setToken(ctx, key, value) {
    if (ctx && ctx.req) {
      ctx[key] = value;
    }

    this.storageProvider.set(key, value, ctx);
  }
  /**
   * Decodes the JWT token
   * @param {Object} ctx
   */


  getUserAccount(ctx) {
    const jwt = this.getJwtToken(ctx);

    if (!jwt) {
      return null;
    }

    let decoded;

    try {
      decoded = (0, _jwtDecode.default)(jwt);
    } catch (ex) {
      return null;
    }
    /* eslint-disable camelcase */


    let {
      first_name,
      last_name
    } = decoded;
    const {
      subscription
    } = decoded;
    const {
      extra,
      promo_user_id,
      scopes
    } = decoded;
    const isAdmin = !!(scopes && scopes.indexOf('admin') !== -1);

    if (extra) {
      const {
        FirstName,
        LastName
      } = extra;
      first_name = FirstName;
      last_name = LastName;
    }
    /* eslint-enable camelcase */


    return {
      user: {
        firstName: first_name,
        lastName: last_name,
        userId: promo_user_id,
        isAdmin,
        subscription: this.parseSubscription(subscription)
      },
      decoded
    };
  }

  parseSubscription(subscription) {
    if (!subscription) {
      return null;
    }

    const {
      plan_id: planId,
      feature_flags: featureFlags
    } = subscription;
    return {
      planId,
      featureFlags
    };
  }
  /**
   * Returns if the user is logged in
   */


  isLoggedIn() {
    return !!this.getJwtToken();
  }

}

var _default = BaseHttpClient;
exports.default = _default;